import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tracks as tracksApi } from "../../lib/api";
import { Button } from "../shared/Button";
import { Modal } from "../shared/Modal";
import { StatusDot } from "../shared/StatusDot";
import { cn } from "../../lib/cn";
import type { AttachTaskRequest, Task, TaskStatus, Track } from "../../lib/types";

interface Props {
  track: Track;
  /** When opened from a task card the task is fixed; from a track it's picked. */
  presetTask?: Task;
  /** Preselect this milestone (e.g. opened from a milestone's "+ Add"). */
  defaultMilestoneIdx?: number;
  onClose: () => void;
}

type Destination =
  | { kind: "existing"; idx: number; acceptance: string }
  | { kind: "new"; title: string; acceptance: string };

/**
 * Attach an existing un-started task to a track. The task either joins an
 * existing milestone or seeds a fresh one — the operator chooses per attach.
 */
export function AttachTaskModal({
  track,
  presetTask,
  defaultMilestoneIdx,
  onClose,
}: Props) {
  const qc = useQueryClient();

  // The track may arrive lean (the list + card payloads omit `plan`), so fetch
  // the full track to populate the milestone picker. Shares the detail cache key
  // so opening from the Track view is instant.
  const { data: fullTrack } = useQuery({
    queryKey: ["tracks", track.id, "detail"],
    queryFn: () => tracksApi.get(track.id),
    initialData: track.plan ? track : undefined,
  });
  const trackData = fullTrack ?? track;
  const milestones = trackData.plan?.milestones ?? [];
  const hasMilestones = milestones.length > 0;

  const { data: attachable = [], isLoading } = useQuery({
    queryKey: ["tracks", track.id, "attachable"],
    queryFn: () => tracksApi.attachableTasks(track.id),
    // Only need the picker when no task is preset.
    enabled: !presetTask,
  });

  const [taskId, setTaskId] = useState<string>(presetTask?.id ?? "");
  const [destTouched, setDestTouched] = useState(false);
  const [dest, setDest] = useState<Destination>({
    kind: "new",
    title: "",
    acceptance: "",
  });

  // Default the destination once the track's milestones are known (the plan can
  // load after mount). Stops as soon as the operator picks, so a manual choice
  // is never clobbered.
  useEffect(() => {
    if (destTouched) return;
    if (hasMilestones) {
      const idx = Math.min(
        Math.max(defaultMilestoneIdx ?? trackData.active_milestone ?? 0, 0),
        milestones.length - 1,
      );
      setDest({ kind: "existing", idx, acceptance: "" });
    } else {
      setDest({ kind: "new", title: "", acceptance: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMilestones, milestones.length, destTouched, defaultMilestoneIdx]);

  const presetTitle = presetTask?.title;

  const attach = useMutation({
    mutationFn: () => {
      const body: AttachTaskRequest =
        dest.kind === "existing"
          ? {
              milestone_idx: dest.idx,
              acceptance: lines(dest.acceptance),
            }
          : {
              new_milestone: {
                title: dest.title.trim(),
                acceptance: lines(dest.acceptance),
              },
            };
      return tracksApi.attachTask(track.id, taskId, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tracks"] });
      qc.invalidateQueries({ queryKey: ["tracks", track.id, "tasks"] });
      qc.invalidateQueries({ queryKey: ["tracks", track.id, "attachable"] });
      qc.invalidateQueries({ queryKey: ["kanban"] });
      onClose();
    },
    onError: (e) => {
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    },
  });

  const newTitleValid =
    dest.kind !== "new" || dest.title.trim().length > 0;
  const canSubmit = !!taskId && newTitleValid && !attach.isPending;

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={!canSubmit}
        onClick={() => attach.mutate()}
      >
        {attach.isPending ? "Adding…" : "Add to track"}
      </Button>
    </div>
  );

  return (
    <Modal
      onClose={onClose}
      size="md"
      title="Add task to track"
      description={`The task becomes part of "${track.title}" and runs when the track is dispatched.`}
      footer={footer}
    >
      <div className="space-y-5">
        {/* Task selection */}
        <div className="space-y-2">
          <div className="text-caption text-text-tertiary">Task</div>
          {presetTask ? (
            <div className="text-body text-text-primary border border-border-subtle rounded-md px-3 py-2">
              {presetTitle}
            </div>
          ) : isLoading ? (
            <div className="text-small text-text-tertiary">Loading eligible tasks…</div>
          ) : attachable.length === 0 ? (
            <div className="text-small text-text-tertiary">
              No eligible tasks. Only un-started (backlog or todo) tasks that
              aren't already in a track can be added.
            </div>
          ) : (
            <ul className="max-h-56 overflow-y-auto divide-y divide-border-subtle border border-border-subtle rounded-md">
              {attachable.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setTaskId(t.id)}
                    className={cn(
                      "w-full text-left px-3 py-2 flex items-center gap-2.5",
                      "hover:bg-bg-hover transition-colors duration-fast ease-out",
                      taskId === t.id && "bg-accent-soft",
                    )}
                  >
                    <StatusDot status={t.status as TaskStatus} />
                    <span className="flex-1 min-w-0 truncate text-body text-text-primary">
                      {t.title}
                    </span>
                    {t.priority && (
                      <span className="text-mono text-text-quaternary">{t.priority}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Destination */}
        <div className="space-y-2">
          <div className="text-caption text-text-tertiary">Milestone</div>
          <div className="flex gap-2">
            <DestTab
              active={dest.kind === "existing"}
              disabled={!hasMilestones}
              onClick={() => {
                setDestTouched(true);
                setDest({
                  kind: "existing",
                  idx: Math.min(
                    Math.max(trackData.active_milestone ?? 0, 0),
                    Math.max(milestones.length - 1, 0),
                  ),
                  acceptance: "",
                });
              }}
            >
              Existing milestone
            </DestTab>
            <DestTab
              active={dest.kind === "new"}
              onClick={() => {
                setDestTouched(true);
                setDest({ kind: "new", title: "", acceptance: "" });
              }}
            >
              New milestone
            </DestTab>
          </div>

          {dest.kind === "existing" ? (
            <div className="space-y-2">
              <select
                value={dest.idx}
                onChange={(e) => {
                  setDestTouched(true);
                  setDest({
                    kind: "existing",
                    idx: Number(e.target.value),
                    acceptance: dest.acceptance,
                  });
                }}
                className={cn(
                  "w-full bg-bg-inset text-text-primary text-body rounded-md",
                  "border border-border-default px-3 py-2",
                  "focus:outline-none focus:ring-1 focus:ring-accent",
                )}
              >
                {milestones.map((m, i) => (
                  <option key={i} value={i}>
                    {`Milestone ${i + 1}`}
                    {m.title ? ` — ${m.title}` : ""}
                  </option>
                ))}
              </select>
              <textarea
                value={dest.acceptance}
                onChange={(e) =>
                  setDest({ ...dest, kind: "existing", acceptance: e.target.value })
                }
                rows={3}
                placeholder="Additional acceptance criteria — one per line (optional)"
                className={cn(
                  "w-full bg-bg-inset text-text-primary text-small rounded-md",
                  "border border-border-default px-3 py-2 resize-y",
                  "focus:outline-none focus:ring-1 focus:ring-accent",
                )}
              />
              <div className="text-small text-text-tertiary">
                Left blank, Maestro adds a criterion from the task title.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                autoFocus
                value={dest.title}
                onChange={(e) => setDest({ ...dest, kind: "new", title: e.target.value })}
                placeholder="New milestone title"
                className={cn(
                  "w-full bg-bg-inset text-text-primary text-body rounded-md",
                  "border border-border-default px-3 py-2",
                  "focus:outline-none focus:ring-1 focus:ring-accent",
                )}
              />
              <textarea
                value={dest.acceptance}
                onChange={(e) =>
                  setDest({ ...dest, kind: "new", acceptance: e.target.value })
                }
                rows={3}
                placeholder="Acceptance criteria — one per line (optional)"
                className={cn(
                  "w-full bg-bg-inset text-text-primary text-small rounded-md",
                  "border border-border-default px-3 py-2 resize-y",
                  "focus:outline-none focus:ring-1 focus:ring-accent",
                )}
              />
              <div className="text-small text-text-tertiary">
                A validator agent checks these after the milestone's tasks finish.
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function lines(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function DestTab({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 text-small rounded-md border transition-colors duration-fast ease-out",
        active
          ? "border-accent-muted bg-accent-soft text-text-primary"
          : "border-border-subtle text-text-secondary hover:bg-bg-hover",
        disabled && "opacity-40 cursor-not-allowed",
      )}
    >
      {children}
    </button>
  );
}
